import { useKeycloak } from '@react-keycloak/web';
import { FC, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { Box, Stack, styled } from '@mui/material';

import useSidemenuStore from '@/app/sidemenu.store';
import AccessDenied from '@/app/ui/AccessDenied/AccessDenied';
// import Guide from '@/app/ui/Guide';
import { GuideProvider } from '@/app/ui/Guide/GuideContext';
import SideMenu from '@/app/ui/SideMenu';
import { ROUTES } from '@/config/routes/paths';
import { useDeploymentDashboardAccess, useIsMobileDashboard } from '@/features/services-dashboard';
import { useAccessDenied } from '@/shared/lib/AccessDeniedContext/AccessDeniedContext';
import { SafeFormProvider } from '@/shared/lib/SafeFormContext/SafeFormContext';
import { NavigationHistoryProvider } from '@/shared/lib/hooks/useNavigationHistory';
import { useSidebarWidth } from '@/shared/lib/hooks/useSidebarWidth';
import { FullPageLoader } from '@/shared/ui/Loading';
import ScrollArea from '@/shared/ui/ScrollArea';

const AuthorizedRoutes: FC = () => {
  const { keycloak, initialized } = useKeycloak();
  const { denied } = useAccessDenied();

  useEffect(() => {
    if (initialized && !keycloak?.authenticated) {
      keycloak.createLoginUrl({ redirectUri: window.location.href }).then((url: string) => {
        window.location.replace(url);
      });
    }
  }, [keycloak, initialized]);

  if (!initialized || !keycloak?.authenticated) return <FullPageLoader />;
  if (denied) return <AccessDenied />;

  return (
    <NavigationHistoryProvider>
      <GuideProvider>
        <SafeFormProvider>
          <AuthorizedLayout />
          {/* <Guide /> */}
        </SafeFormProvider>
      </GuideProvider>
    </NavigationHistoryProvider>
  );
};

const AuthorizedLayout: FC = () => {
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const sidebarWidth = useSidebarWidth(sidebarRef);
  const setSidebarWidth = useSidemenuStore((state) => state.setSidebarWidth);
  const { pathname } = useLocation();
  const dashboardAccess = useDeploymentDashboardAccess();
  const isMobile = useIsMobileDashboard();
  const hideSideMenu =
    isMobile &&
    pathname === ROUTES.AUTHORIZED.LANDING &&
    (dashboardAccess.hasAccess || dashboardAccess.isResolving);

  useEffect(() => {
    setSidebarWidth(sidebarWidth);
  }, [sidebarWidth, setSidebarWidth]);

  return (
    <RootContainer>
      <Box display={hideSideMenu ? 'none' : 'contents'}>
        <SideMenu ref={sidebarRef} />
      </Box>

      <MainStack sidebarWidth={hideSideMenu ? 0 : sidebarWidth}>
        <PageRoot id="page-root">
          {(hideSideMenu || !!sidebarWidth) && (
            <ContentWrapper>
              <ScrollArea>
                <Outlet />
              </ScrollArea>
            </ContentWrapper>
          )}
        </PageRoot>
      </MainStack>
    </RootContainer>
  );
};

export default AuthorizedRoutes;

const RootContainer = styled(Stack)({
  flexDirection: 'row',
  height: '100vh',
  width: '100vw',
  padding: 0,
  margin: 0,
  overflow: 'hidden',
  background: '#F9F7FC',
});

const PageRoot = styled(Stack)({
  height: '100vh',
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const ContentWrapper = styled(Box)({
  padding: 0,
  height: '100vh',
  overflow: 'hidden',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const MainStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'sidebarWidth',
})<{ sidebarWidth: number }>(({ sidebarWidth }) => ({
  flex: 1,
  maxWidth: `calc(100vw - ${sidebarWidth}px)`,
}));
